"use client";

import { useState } from "react";
import { MenuCheckRow, MenuPanel, MenuTrigger, TriCheckbox, useDismissableMenu } from "@/components/finance/menu";

/**
 * A node in the category checkbox tree. A node owns the full set of leaf
 * categories beneath it (`cats`); leaves have a single-element `cats` and no
 * `children`. Selection is tracked as a set of leaf category values, so a
 * parent checkbox is checked when all its leaves are selected and indeterminate
 * when only some are.
 */
export interface CatNode {
  key: string;
  label: string;
  cats: string[];
  children?: CatNode[];
}

function nodeState(node: CatNode, selected: Set<string>): { all: boolean; some: boolean } {
  let n = 0;
  for (const c of node.cats) if (selected.has(c)) n++;
  return { all: n > 0 && n === node.cats.length, some: n > 0 && n < node.cats.length };
}

function NodeRows({
  node,
  depth,
  selected,
  onToggle,
  onOnly,
  expanded,
  onExpand,
}: {
  node: CatNode;
  depth: number;
  selected: Set<string>;
  onToggle: (node: CatNode) => void;
  onOnly: (node: CatNode) => void;
  expanded: Set<string>;
  onExpand: (key: string) => void;
}) {
  const { all, some } = nodeState(node, selected);
  const hasChildren = !!node.children?.length;
  const isOpen = expanded.has(node.key);
  return (
    <>
      <MenuCheckRow
        label={node.label}
        checked={all}
        indeterminate={some}
        onToggle={() => onToggle(node)}
        onOnly={() => onOnly(node)}
        indent={depth}
        emphasis={depth === 0}
        expand={hasChildren ? { open: isOpen, onToggle: () => onExpand(node.key) } : undefined}
        dataKey={node.key}
      />
      {hasChildren && isOpen && node.children!.map((ch) => (
        <NodeRows
          key={ch.key}
          node={ch}
          depth={depth + 1}
          selected={selected}
          onToggle={onToggle}
          onOnly={onOnly}
          expanded={expanded}
          onExpand={onExpand}
        />
      ))}
    </>
  );
}

/**
 * Checkbox-tree category picker. A master "select all / none" box sits on top,
 * then a nested checkbox tree — so you can start from everything selected and
 * deselect the few you don't want (the inverse was awkward with the old
 * click-to-scope cascade). Hovering any row reveals "Only", which narrows the
 * selection to that row alone (a group → its categories; a leaf → itself).
 * Selection is a set of leaf category values; `allCats` is every selectable
 * leaf (drives the master box + the button summary). The host owns
 * persistence / URL sync.
 */
export function CategoryMenu({
  tree,
  allCats,
  selected,
  onChange,
  label = "All categories",
  sizeClass,
}: {
  tree: CatNode[];
  allCats: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  label?: string;
  /** Trigger padding + text size override (default: the toolbar pill size). */
  sizeClass?: string;
}) {
  const menu = useDismissableMenu();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(tree.map((n) => n.key)));

  const allSel = allCats.length > 0 && allCats.every((c) => selected.has(c));
  const noneSel = selected.size === 0;

  const toggleNode = (node: CatNode) => {
    const next = new Set(selected);
    const { all } = nodeState(node, selected);
    if (all) for (const c of node.cats) next.delete(c);
    else for (const c of node.cats) next.add(c);
    onChange(next);
  };
  const onlyNode = (node: CatNode) => onChange(new Set(node.cats));
  const toggleAll = () => onChange(allSel ? new Set() : new Set(allCats));
  const onExpand = (key: string) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const btnLabel = allSel
    ? label
    : noneSel
      ? "No categories"
      : `${selected.size} of ${allCats.length} categories`;

  return (
    <div ref={menu.ref} className="relative">
      <MenuTrigger open={menu.open} onClick={menu.toggle} sizeClass={sizeClass} className="max-w-72">
        <span className="truncate">{btnLabel}</span>
      </MenuTrigger>

      {menu.open && (
        <MenuPanel>
          <div className="flex items-center justify-between border-b border-edge px-3 py-2">
            <label className="flex cursor-pointer items-center gap-2">
              <TriCheckbox checked={allSel} indeterminate={!allSel && !noneSel} onChange={toggleAll} />
              <span className="text-sm font-medium">{allSel ? "All selected" : "Select all"}</span>
            </label>
            {!noneSel && (
              <button type="button" onClick={() => onChange(new Set())} className="text-[11px] font-medium text-accent-ink hover:underline">
                Clear
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto py-1">
            {tree.map((n) => (
              <NodeRows
                key={n.key}
                node={n}
                depth={0}
                selected={selected}
                onToggle={toggleNode}
                onOnly={onlyNode}
                expanded={expanded}
                onExpand={onExpand}
              />
            ))}
          </div>
        </MenuPanel>
      )}
    </div>
  );
}
